import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEcran } from './useEcran';
import { jouerSonNotification, sourdineActive, basculerSourdine } from './sonNotification';

/* =============================================================================
   PLAN DE TRAVAIL — espace d'analyse flottant, multi-pages
   ─────────────────────────────────────────────────────────────────────────
   L'utilisateur epingle n'importe quelle stat / cercle / graphe du dashboard.
   Chaque element epingle est un INSTANTANE FIGE : il emporte une copie des
   donnees ET la periode avec laquelle il a ete capture. Changer ensuite le
   filtre de dates et epingler autre chose n'ecrase rien — les elements
   s'accumulent, chacun gardant sa propre periode. C'est tout l'interet :
   comparer aout et septembre cote a cote sur le meme plan.

   PAGES : plusieurs plans coexistent, nommables, pour preparer une reunion
   par sujet (une page par boutique, par mois, par point d'ordre du jour...).

   MODE : l'espace bascule entre fond nuit et fond clair — le nuit pour
   travailler a l'ecran, le clair pour projeter ou imprimer.

   Ce module ne connait AUCUN graphe : il recoit un `registre` qui associe un
   type d'element a une fonction de rendu `(donnees, mode) => JSX`. C'est ce
   qui permet de n'y stocker que des donnees serialisables.
   ============================================================================= */

/* Le plan est range PAR UTILISATEUR. Sans cela, sur un poste partage (le cas
   en boutique), la deconnexion ne vidant que le jeton et le profil, la
   personne suivante retrouvait les plans de la precedente. */
const CLE_BASE = 'peak_plan_travail_v2';
const CLE_NON_ATTRIBUEE = CLE_BASE;          // ancien emplacement, sans utilisateur
const CLE_ANCIENNE = 'peak_plan_travail';    // format v1 : un plan unique, tableau brut

const utilisateurCourant = () => {
  try {
    const u = JSON.parse(localStorage.getItem('peak_user'));
    return u?.email || 'anonyme';
  } catch (e) {
    return 'anonyme';
  }
};
const cleDe = (email) => `${CLE_BASE}:${email}`;
const MAX_CARTES = 24;
const MAX_PAGES = 8;

/* La largeur est imposee (le graphe s'y adapte), la hauteur ne l'est PAS :
   un element epingle fait exactement la taille de son contenu. HAUTEUR n'est
   donc qu'une estimation, utilisee pour espacer les elements a l'arrivee et
   dimensionner la toile defilable. */
const LARGEUR = { normal: 316, grand: 620 };
const HAUTEUR = { normal: 340, grand: 300 };

/* Deux ambiances. Les graphes n'ayant ni cadre ni fond, c'est le voile qui
   leur sert de support : il doit rester dense dans les deux modes, sinon le
   dashboard floute transparait au travers des courbes. */
const PALETTES = {
  sombre: {
    voile: 'rgba(4,10,15,.93)',
    panneau: 'rgba(10,22,30,.96)',
    surface: 'rgba(16,32,42,.96)',
    bord: 'rgba(90,215,235,.20)',
    bordFort: 'rgba(90,215,235,.55)',
    accent: '#5ad7eb',
    accentSourd: 'rgba(90,215,235,.10)',
    texte: '#dceef4',
    faible: '#7c98a5',
    danger: '#ff7a6b',
    grille: 'rgba(90,215,235,.045)',
    ombre: '0 0 0 1px rgba(90,215,235,.14), 0 18px 44px rgba(0,0,0,.55)',
    ombreGlisse: '0 14px 26px rgba(0,0,0,.65)',
    // Les notes, elles, ont besoin d'un fond : sans support visible on ne
    // saurait pas ou cliquer pour ecrire.
    noteFond: 'rgba(46,38,16,.92)',
    noteBord: 'rgba(224,166,60,.38)',
    noteTexte: '#f0dfb4',
    noteAccent: '#e0a63c',
  },
  clair: {
    voile: 'rgba(243,245,247,.96)',
    panneau: 'rgba(255,255,255,.97)',
    surface: '#ffffff',
    bord: '#dfe4e9',
    bordFort: '#0f6d80',
    accent: '#0f6d80',
    accentSourd: 'rgba(15,109,128,.09)',
    texte: '#111827',
    faible: '#6b7280',
    danger: '#a42a1f',
    grille: 'rgba(15,109,128,.06)',
    ombre: '0 1px 2px rgba(16,24,40,.06), 0 8px 24px rgba(16,24,40,.10)',
    ombreGlisse: '0 12px 24px rgba(16,24,40,.22)',
    noteFond: '#fdf3d4',
    noteBord: '#e3cf95',
    noteTexte: '#4a3c14',
    noteAccent: '#92620d',
  },
};

const LARGEUR_NOTE = { normal: 262, grand: 524 };
const HAUTEUR_NOTE = { normal: 156, grand: 250 };

const MONO = 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace';

const nouvelId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const horodatage = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const etatNeuf = () => {
  const id = nouvelId();
  return { pages: [{ id, nom: 'Plan 1', cartes: [] }], active: id, mode: 'sombre' };
};

const enPages = (cartes) => {
  const id = nouvelId();
  return { pages: [{ id, nom: 'Plan 1', cartes }], active: id, mode: 'sombre' };
};

/* Reprise des emplacements precedents : personne ne doit perdre son travail
   a la mise a jour. Les anciens emplacements, partages entre utilisateurs,
   sont adoptes par le premier connecte puis effaces. */
function lireEtat(email) {
  try {
    const v = JSON.parse(localStorage.getItem(cleDe(email)));
    if (v && Array.isArray(v.pages) && v.pages.length) return v;
  } catch (e) { /* illisible : on regarde les emplacements precedents */ }
  try {
    const v = JSON.parse(localStorage.getItem(CLE_NON_ATTRIBUEE));
    if (v && Array.isArray(v.pages) && v.pages.length) {
      localStorage.removeItem(CLE_NON_ATTRIBUEE);
      return v;
    }
  } catch (e) { /* rien a reprendre */ }
  try {
    const anciennes = JSON.parse(localStorage.getItem(CLE_ANCIENNE));
    if (Array.isArray(anciennes) && anciennes.length) {
      localStorage.removeItem(CLE_ANCIENNE);
      return enPages(anciennes);
    }
  } catch (e) { /* rien a reprendre */ }
  return etatNeuf();
}

/* --- Alignement pendant le deplacement ------------------------------------
   Un tableau de bord lisible tient a l'alignement de ses blocs. Le faire a
   l'oeil, au pixel pres, est impossible : on approche des reperes.

   Trois repes par axe sont proposes — bord debut, centre, bord fin — soit
   ceux qu'on utilise reellement pour composer une grille. Quand la position
   tombe a moins de SEUIL_AIMANT du repere, elle y est calee et un trait le
   materialise, etendu jusqu'aux elements concernes pour qu'on voie SUR QUOI
   l'on s'aligne, et pas seulement qu'on s'aligne. */
const SEUIL_AIMANT = 7;

function aimanter(x, y, largeur, hauteur, autres) {
  const reperes = [];
  let ax = x;
  let ay = y;

  // Candidats : {position visee, position du repere, elements concernes}
  const candidatsX = [];
  const candidatsY = [];
  autres.forEach((o) => {
    // Alignement de bord a bord et de centre a centre.
    candidatsX.push({ vise: o.x, trait: o.x, o }, { vise: o.x + o.l - largeur, trait: o.x + o.l, o },
      { vise: o.x + o.l / 2 - largeur / 2, trait: o.x + o.l / 2, o });
    candidatsY.push({ vise: o.y, trait: o.y, o }, { vise: o.y + o.h - hauteur, trait: o.y + o.h, o },
      { vise: o.y + o.h / 2 - hauteur / 2, trait: o.y + o.h / 2, o });
    // Mise bout a bout : on colle a droite ou en dessous d'un voisin, avec la
    // gouttiere habituelle, ce qui donne des rangees regulieres sans effort.
    candidatsX.push({ vise: o.x + o.l + 24, trait: null, o });
    candidatsY.push({ vise: o.y + o.h + 24, trait: null, o });
  });

  const meilleur = (candidats, valeur) => candidats
    .map((c) => ({ ...c, ecart: Math.abs(c.vise - valeur) }))
    .filter((c) => c.ecart <= SEUIL_AIMANT)
    .sort((a, b) => a.ecart - b.ecart)[0];

  const mx = meilleur(candidatsX, x);
  if (mx) {
    ax = Math.max(0, mx.vise);
    if (mx.trait !== null) {
      const de = Math.min(ay, mx.o.y);
      const a = Math.max(ay + hauteur, mx.o.y + mx.o.h);
      reperes.push({ axe: 'x', position: mx.trait, de, a });
    }
  }
  const my = meilleur(candidatsY, y);
  if (my) {
    ay = Math.max(0, my.vise);
    if (my.trait !== null) {
      const de = Math.min(ax, my.o.x);
      const a = Math.max(ax + largeur, my.o.x + my.o.l);
      reperes.push({ axe: 'y', position: my.trait, de, a });
    }
  }
  return { x: ax, y: ay, reperes };
}

const Ctx = createContext(null);
export const usePlanDeTravail = () => useContext(Ctx);

/* --- Provider : detient les pages, le mode, et rend la surimpression ------ */
export function PlanDeTravailProvider({ registre, comparateur, rejoueur, rejoueurCarte, deduireRequete, boutiques = [], apiBase = '', children }) {
  // L'utilisateur est lu au montage. Suffisant : une deconnexion demonte tout
  // l'arbre (App renvoie l'ecran de connexion), donc le provider est reconstruit
  // avec le bon compte a la connexion suivante.
  const [email] = useState(utilisateurCourant);
  const [etat, setEtat] = useState(() => lireEtat(email));
  const [ouvert, setOuvert] = useState(false);
  const [echecStockage, setEchecStockage] = useState(null);
  const [synchro, setSynchro] = useState('chargement'); // chargement | envoi | ok | hors-ligne
  const [pret, setPret] = useState(false);              // le serveur a repondu, on peut ecrire

  const pageActive = etat.pages.find((p) => p.id === etat.active) || etat.pages[0];
  // Memoise : sans cela le `|| []` fabrique un tableau neuf a chaque rendu et
  // annule la memoisation de la valeur de contexte plus bas.
  const cartes = useMemo(() => pageActive?.cartes || [], [pageActive]);
  // Les pages creees avant l'arrivee des notes n'ont pas le champ.
  const notes = useMemo(() => pageActive?.notes || [], [pageActive]);
  const mode = etat.mode === 'clair' ? 'clair' : 'sombre';
  const P = PALETTES[mode];

  // Le provider peut etre remonte : on repart du plus haut niveau deja
  // attribue, sinon un element ancien resterait au-dessus de ceux qu'on
  // remonte ensuite.
  const zRef = useRef(null);
  if (zRef.current === null) {
    zRef.current = Math.max(10, ...etat.pages.flatMap((p) => [
      ...p.cartes.map((c) => c.z || 0),
      ...(p.notes || []).map((n) => n.z || 0),
    ]));
  }

  /* Copie locale : elle sert de cache hors ligne et d'affichage immediat au
     demarrage, avant que le serveur ait repondu. Un echec d'ecriture (quota
     depasse, navigation privee) ne doit PAS etre avale silencieusement. */
  useEffect(() => {
    try {
      localStorage.setItem(cleDe(email), JSON.stringify(etat));
      setEchecStockage(null);
    } catch (e) {
      setEchecStockage("Espace de stockage local sature : la copie hors ligne n'est plus a jour.");
    }
  }, [etat, email]);

  /* --- Synchronisation serveur ------------------------------------------
     Le serveur fait autorite : c'est lui qui permet de retrouver son plan
     depuis un autre poste. Au demarrage on affiche la copie locale (instantane)
     puis on la remplace par celle du serveur des qu'elle arrive. */
  useEffect(() => {
    let annule = false;
    const jeton = localStorage.getItem('peak_token');
    if (!jeton) { setPret(true); return undefined; }
    setSynchro('chargement');
    fetch(`${apiBase}/api/plan`, { headers: { Authorization: `Bearer ${jeton}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => {
        if (annule) return;
        if (j.contenu && Array.isArray(j.contenu.pages) && j.contenu.pages.length) setEtat(j.contenu);
        setSynchro('ok');
      })
      .catch(() => { if (!annule) setSynchro('hors-ligne'); })
      // `pret` n'est leve qu'ici : tant que la reponse du serveur n'est pas
      // arrivee, on n'envoie rien. Sans ce verrou, l'etat local initial (souvent
      // vide) ecraserait le plan deja enregistre en base.
      .finally(() => { if (!annule) setPret(true); });
    return () => { annule = true; };
  }, [apiBase]);

  useEffect(() => {
    if (!pret) return undefined;
    const jeton = localStorage.getItem('peak_token');
    if (!jeton) return undefined;
    // Temporise : deplacer un element ou renommer une page ne doit pas declencher
    // une requete par frappe.
    const t = setTimeout(() => {
      setSynchro('envoi');
      fetch(`${apiBase}/api/plan`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jeton}` },
        body: JSON.stringify({ contenu: etat }),
      })
        .then((r) => setSynchro(r.ok ? 'ok' : 'hors-ligne'))
        .catch(() => setSynchro('hors-ligne'));
    }, 900);
    return () => clearTimeout(t);
  }, [etat, pret, apiBase]);

  /* Toutes les mutations d'elements passent par ici : elles ne touchent que
     la page active, ce qui evite de repeter la traversee des pages.

     Le VERROU est applique ici, en un seul endroit. Le poser sur chaque bouton
     de l'interface serait a la fois fastidieux et fragile — il suffirait
     d'oublier un chemin pour que la protection saute. Une page verrouillee est
     donc integralement figee, quel que soit le geste. */
  const majCartes = useCallback((fn) => {
    setEtat((e) => ({
      ...e,
      pages: e.pages.map((p) => (p.id === e.active && !p.verrouillee ? { ...p, cartes: fn(p.cartes) } : p)),
    }));
  }, []);

  const epingler = useCallback((item) => {
    majCartes((prev) => {
      if (prev.length >= MAX_CARTES) return prev;
      // Grille de depart en cascade : trois colonnes, puis on descend.
      const i = prev.length;
      const pos = { x: 26 + (i % 3) * (LARGEUR.normal + 24), y: 26 + Math.floor(i / 3) * (HAUTEUR.normal + 24) };
      return [...prev, {
        ...item,
        id: nouvelId(),
        capture: horodatage(),
        pos,
        // Les graphes larges (heatmap, courbes) arrivent deja en grand format.
        taille: item.taille === 'grand' ? 'grand' : 'normal',
        z: ++zRef.current,
      }];
    });
  }, [majCartes]);

  const retirer = useCallback((id) => majCartes((p) => p.filter((c) => c.id !== id)), [majCartes]);
  const vider = useCallback(() => majCartes(() => []), [majCartes]);
  const majCarte = useCallback((id, patch) => {
    majCartes((p) => p.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }, [majCartes]);
  const auPremierPlan = useCallback((id) => majCarte(id, { z: ++zRef.current }), [majCarte]);

  /* --- Notes libres ------------------------------------------------------
     Meme mecanique que les elements epingles (position, plan, suppression),
     mais leur contenu est saisi et non capture. Elles vivent dans la page :
     changer d'onglet change de jeu de notes. */
  const majNotes = useCallback((fn) => {
    setEtat((e) => ({
      ...e,
      pages: e.pages.map((p) => (p.id === e.active && !p.verrouillee ? { ...p, notes: fn(p.notes || []) } : p)),
    }));
  }, []);

  const ajouterNote = useCallback(() => {
    majNotes((prev) => {
      const i = prev.length;
      return [...prev, {
        id: nouvelId(),
        texte: '',
        // Posees en bas de l'espace pour ne pas recouvrir les graphes deja la.
        pos: { x: 26 + (i % 4) * (LARGEUR_NOTE.normal + 18), y: 26 },
        taille: 'normal',
        z: ++zRef.current,
      }];
    });
  }, [majNotes]);

  const majNote = useCallback((id, patch) => {
    majNotes((p) => p.map((n) => (n.id === id ? { ...n, ...patch } : n)));
  }, [majNotes]);
  const retirerNote = useCallback((id) => majNotes((p) => p.filter((n) => n.id !== id)), [majNotes]);
  const noteAuPremierPlan = useCallback((id) => majNote(id, { z: ++zRef.current }), [majNote]);

  /* Renvoie l'identifiant de la page creee : l'appelant s'en sert pour ouvrir
     aussitot le champ de nom, afin qu'on baptise la page en la creant plutot
     que de decouvrir le renommage par hasard. */
  const ajouterPage = useCallback(() => {
    const id = nouvelId();
    setEtat((e) => {
      if (e.pages.length >= MAX_PAGES) return e;
      return { ...e, pages: [...e.pages, { id, nom: `Plan ${e.pages.length + 1}`, cartes: [] }], active: id };
    });
    return id;
  }, []);

  const choisirPage = useCallback((id) => setEtat((e) => ({ ...e, active: id })), []);

  /* Verrouiller : une page de reunion validee ne doit plus bouger. Un clic
     malheureux sur une croix, un element deplace par megarde pendant qu'on
     presente, et le travail est perdu. */
  const basculerVerrou = useCallback((id) => {
    setEtat((e) => ({
      ...e,
      pages: e.pages.map((p) => (p.id === id ? { ...p, verrouillee: !p.verrouillee } : p)),
    }));
  }, []);

  /* Dupliquer : repartir d'une composition existante pour le mois suivant
     plutot que de tout recomposer. La copie est INDEPENDANTE — nouveaux
     identifiants pour la page comme pour chaque element, sans quoi deplacer
     un graphe dans l'une le deplacerait dans l'autre. Elle n'herite pas du
     verrou : on duplique justement pour retravailler. */
  /* Rejouer : creer une page identique en composition, mais dont chaque
     element a repose sa question sur une autre periode. C'est ce qui donne
     tout son sens a la duplication — sans cela, une copie ne contient que les
     chiffres du mois d'origine.

     Le resultat est une NOUVELLE page : l'originale reste intacte, ce qui
     permet de poser les deux cote a cote et de laisser les ecarts se
     calculer. */
  const rejouerSur = useCallback(async (id, from, to) => {
    if (!rejoueur) throw new Error('Rejeu indisponible');
    const source = etat.pages.find((p) => p.id === id);
    if (!source) throw new Error('Page introuvable');
    if (etat.pages.length >= MAX_PAGES) {
      throw new Error(`Vous avez deja ${MAX_PAGES} pages : supprimez-en une avant de rejouer.`);
    }

    const { cartes: nouvelles, libelle, rejoues, conserves } = await rejoueur(source, { from, to });
    const nouveau = nouvelId();
    setEtat((e) => {
      const copie = {
        ...source,
        id: nouveau,
        nom: `${source.nom.replace(/\s*\(copie\)$/, '')} — ${libelle}`,
        verrouillee: false,
        envoiId: undefined,
        expediteur: undefined,
        cartes: nouvelles.map((c) => ({ ...c, id: nouvelId(), z: ++zRef.current })),
        notes: (source.notes || []).map((n) => ({ ...n, id: nouvelId(), z: ++zRef.current })),
      };
      const i = e.pages.findIndex((p) => p.id === id);
      const pages = [...e.pages];
      pages.splice(i + 1, 0, copie);
      return { ...e, pages, active: nouveau };
    });
    return { rejoues, conserves, libelle };
  }, [rejoueur, etat.pages]);

  /* Rejouer UN element : sa periode, sa boutique, ou les deux.

     `aCote` cree une copie posee immediatement a droite de l'original plutot
     que de le remplacer. C'est le geste utile : deux fois la meme mesure, sur
     deux boutiques ou deux mois, voisines — et l'ecart s'affiche de lui-meme
     entre les deux. */
  const rejouerCarte = useCallback(async (id, params, { aCote } = {}) => {
    if (!rejoueurCarte) throw new Error('Rejeu indisponible');
    const source = cartes.find((c) => c.id === id);
    if (!source) throw new Error('Element introuvable');

    const maj = await rejoueurCarte(source, params);
    majCartes((prev) => {
      const i = prev.findIndex((c) => c.id === id);
      if (i < 0) return prev;
      const rejoue = { ...prev[i], ...maj, indisponible: null };
      if (!aCote) {
        const copie = [...prev];
        copie[i] = rejoue;
        return copie;
      }
      // Pose a droite, sur la meme ligne : c'est la disposition que la
      // comparaison automatique reconnait.
      const largeur = LARGEUR[source.taille] || LARGEUR.normal;
      return [...prev, {
        ...rejoue,
        id: nouvelId(),
        capture: horodatage(),
        pos: { x: source.pos.x + largeur + 24, y: source.pos.y },
        z: ++zRef.current,
      }];
    });
    return maj;
  }, [rejoueurCarte, cartes, majCartes]);

  const dupliquerPage = useCallback((id) => {
    const nouveau = nouvelId();
    setEtat((e) => {
      if (e.pages.length >= MAX_PAGES) return e;
      const source = e.pages.find((p) => p.id === id);
      if (!source) return e;
      const copie = {
        ...source,
        id: nouveau,
        nom: `${source.nom} (copie)`,
        verrouillee: false,
        // Une copie n'est pas une page recue : elle appartient a celui qui la
        // fait, et ne doit pas rester marquee du nom d'un collegue.
        envoiId: undefined,
        expediteur: undefined,
        cartes: (source.cartes || []).map((c) => ({ ...c, id: nouvelId(), z: ++zRef.current })),
        notes: (source.notes || []).map((n) => ({ ...n, id: nouvelId(), z: ++zRef.current })),
      };
      const i = e.pages.findIndex((p) => p.id === id);
      const pages = [...e.pages];
      pages.splice(i + 1, 0, copie);   // juste apres l'originale
      return { ...e, pages, active: nouveau };
    });
    return nouveau;
  }, []);
  const renommerPage = useCallback((id, nom) => {
    setEtat((e) => ({ ...e, pages: e.pages.map((p) => (p.id === id ? { ...p, nom: nom.trim() || p.nom } : p)) }));
  }, []);
  const supprimerPage = useCallback((id) => {
    setEtat((e) => {
      if (e.pages.length <= 1) return e; // il reste toujours au moins une page
      const pages = e.pages.filter((p) => p.id !== id);
      return { ...e, pages, active: e.active === id ? pages[0].id : e.active };
    });
  }, []);

  const basculerMode = useCallback(() => {
    setEtat((e) => ({ ...e, mode: e.mode === 'clair' ? 'sombre' : 'clair' }));
  }, []);

  /* --- Envoi d'une page a des collegues ----------------------------------
     Ce qui part est une COPIE FIGEE : continuer a travailler sur sa page ne
     doit pas modifier ce qu'ont recu les autres, sinon les chiffres discutes
     en reunion changeraient sous leurs yeux. */
  const appelAuth = useCallback(async (chemin, options = {}) => {
    const jetonAuth = localStorage.getItem('peak_token');
    if (!jetonAuth) throw new Error('Session expiree, reconnectez-vous');
    const r = await fetch(`${apiBase}${chemin}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jetonAuth}`, ...(options.headers || {}) },
    });
    if (!r.ok) {
      const corps = await r.json().catch(() => ({}));
      throw new Error(corps.detail || `Le serveur a repondu ${r.status}`);
    }
    return r.json();
  }, [apiBase]);

  const chargerCollegues = useCallback(
    () => appelAuth('/api/plan/collegues').then((d) => d.collegues || []),
    [appelAuth]
  );

  const envoyerPage = useCallback(async (destinataires) => {
    if (!pageActive) throw new Error('Aucune page a envoyer');
    const d = await appelAuth('/api/plan/envois', {
      method: 'POST',
      body: JSON.stringify({ destinataires, page: pageActive, mode }),
    });
    return d.envoyes;
  }, [appelAuth, pageActive, mode]);

  /* --- Reception ---------------------------------------------------------
     Les envois recus font office de notifications. Ils restent consultables
     meme apres que la page a ete retiree du plan : un clic malheureux ne doit
     pas faire perdre le travail recu d'un collegue. */
  const [envois, setEnvois] = useState([]);

  const rafraichirEnvois = useCallback(() => {
    if (!localStorage.getItem('peak_token')) return Promise.resolve([]);
    return appelAuth('/api/plan/envois')
      .then((d) => { setEnvois(d.envois || []); return d.envois || []; })
      .catch(() => []);   // hors ligne : on garde la liste precedente
  }, [appelAuth]);

  /* Reception instantanee. Le serveur POUSSE la notification par WebSocket :
     aucun sondage, le delai se compte en millisecondes.

     Le sondage de secours est conserve, mais espace : derriere un proxy mal
     configure la WebSocket ne s'etablit pas, et mieux vaut une notification
     tardive qu'aucune notification. */
  useEffect(() => {
    rafraichirEnvois();
    const t = setInterval(rafraichirEnvois, 120000);
    return () => clearInterval(t);
  }, [rafraichirEnvois]);

  useEffect(() => {
    const jetonAuth = localStorage.getItem('peak_token');
    if (!jetonAuth || typeof WebSocket === 'undefined') return undefined;

    let socket = null;
    let minuteur = null;
    let ferme = false;
    let attente = 2000;   // delai avant reconnexion, double a chaque echec

    const connecter = () => {
      if (ferme) return;
      const protocole = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // apiBase est vide en developpement (le serveur relaie) comme en
      // production (meme domaine) : on part donc de l'adresse de la page.
      const base = apiBase
        ? apiBase.replace(/^http/, 'ws')
        : `${protocole}//${window.location.host}`;
      try {
        socket = new WebSocket(`${base}/api/ws/notifications`);
      } catch (e) {
        return;   // adresse invalide : on s'en tient au sondage
      }

      socket.onopen = () => {
        // Le jeton passe par le premier message et non par l'adresse : dans
        // l'URL il finirait dans les journaux du proxy et dans l'historique.
        socket.send(JSON.stringify({ token: jetonAuth }));
        attente = 2000;
      };

      socket.onmessage = (evt) => {
        let msg;
        try { msg = JSON.parse(evt.data); } catch (e) { return; }
        if (msg.type !== 'nouvel_envoi') return;
        rafraichirEnvois().then((liste) => {
          // On ne sonne que s'il reste effectivement quelque chose de non lu :
          // inutile de faire du bruit si la page a deja ete ouverte ailleurs.
          if (liste.some((e) => !e.lu)) jouerSonNotification();
        });
      };

      socket.onclose = () => {
        if (ferme) return;
        // Reconnexion progressive : une coupure reseau ne doit pas se traduire
        // par un martelement du serveur.
        minuteur = setTimeout(connecter, attente);
        attente = Math.min(attente * 2, 60000);
      };
    };

    connecter();
    return () => {
      ferme = true;
      clearTimeout(minuteur);
      if (socket) { socket.onclose = null; socket.close(); }
    };
  }, [apiBase, rafraichirEnvois]);

  /* Ouvre un envoi : la page recue devient une page du plan du destinataire,
     signee du nom de l'expediteur. Si elle y est deja, on s'y rend au lieu de
     la dupliquer. */
  const ouvrirEnvoi = useCallback(async (envoiId) => {
    const d = await appelAuth(`/api/plan/envois/${envoiId}`);
    setEtat((e) => {
      const existante = e.pages.find((p) => p.envoiId === envoiId);
      if (existante) return { ...e, active: existante.id };
      if (e.pages.length >= MAX_PAGES) {
        throw new Error(`Vous avez deja ${MAX_PAGES} pages : supprimez-en une avant d'ouvrir celle-ci.`);
      }
      const id = nouvelId();
      const recue = {
        ...d.page,
        id,
        nom: d.titre,
        // Marques de provenance : l'onglet s'affiche alors au nom de
        // l'expediteur, en rouge.
        envoiId,
        expediteur: d.expediteur,
        recuLe: d.envoye_le,
      };
      return { ...e, pages: [...e.pages, recue], active: id };
    });
    setEnvois((l) => l.map((x) => (x.id === envoiId ? { ...x, lu: true } : x)));
    setOuvert(true);
  }, [appelAuth]);

  const ecarterEnvoi = useCallback(async (envoiId) => {
    await appelAuth(`/api/plan/envois/${envoiId}`, { method: 'DELETE' });
    setEnvois((l) => l.filter((x) => x.id !== envoiId));
  }, [appelAuth]);

  const valeur = useMemo(() => ({
    cartes, epingler, retirer, vider, majCarte, auPremierPlan,
    notes, ajouterNote, majNote, retirerNote, noteAuPremierPlan,
    pages: etat.pages, pageActive: etat.active, ajouterPage, choisirPage, renommerPage, supprimerPage,
    basculerVerrou, dupliquerPage, rejouerSur, rejouerCarte, boutiques, deduireRequete,
    peutRejouer: !!rejoueur, peutRejouerCarte: !!rejoueurCarte,
    verrouillee: !!pageActive?.verrouillee,
    mode, P, basculerMode, echecStockage, synchro,
    chargerCollegues, envoyerPage, envois, rafraichirEnvois, ouvrirEnvoi, ecarterEnvoi,
    ouvert, setOuvert, plein: cartes.length >= MAX_CARTES,
  }), [cartes, epingler, retirer, vider, majCarte, auPremierPlan,
    notes, ajouterNote, majNote, retirerNote, noteAuPremierPlan, etat.pages, etat.active,
    ajouterPage, choisirPage, renommerPage, supprimerPage, basculerVerrou, dupliquerPage,
    rejouerSur, rejoueur, rejouerCarte, rejoueurCarte, boutiques, deduireRequete,
    pageActive, mode, P, basculerMode,
    echecStockage, synchro, chargerCollegues, envoyerPage, envois, rafraichirEnvois,
    ouvrirEnvoi, ecarterEnvoi, ouvert]);

  return (
    <Ctx.Provider value={valeur}>
      {children}
      <Lanceur />
      {ouvert && <Surimpression registre={registre} comparateur={comparateur} />}
    </Ctx.Provider>
  );
}

/* --- Petit bouton "+" pose sur un graphe : capture l'instantane -----------
   Toujours habille en clair : il vit sur le dashboard, jamais dans le plan. */
export function BoutonEpingler({ fabriquer, position, enFlux, titreBouton = 'Ajouter au plan de travail' }) {
  const plan = usePlanDeTravail();
  const { estMobile } = useEcran();
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(false), 1100);
    return () => clearTimeout(t);
  }, [flash]);
  if (!plan) return null;

  // Sur une page verrouillee l'epinglage serait refuse sans rien dire : mieux
  // vaut un bouton visiblement inactif et une infobulle qui explique pourquoi.
  const bloque = plan.plein || plan.verrouillee;
  const raison = plan.verrouillee
    ? 'Page verrouillee : deverrouillez-la pour y ajouter des elements'
    : (plan.plein ? 'Plan de travail plein' : titreBouton);

  const contenu = (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); if (bloque) return; plan.epingler(fabriquer()); setFlash(true); }}
        title={raison}
        disabled={bloque}
        style={{
          // Cible elargie au doigt : 20 pixels sont sous le minimum touchable.
          width: estMobile ? 28 : 20, height: estMobile ? 28 : 20,
          borderRadius: '50%', padding: 0, cursor: bloque ? 'not-allowed' : 'pointer',
          border: `1px solid ${flash ? '#1c7a42' : '#e4e7eb'}`,
          background: flash ? '#1c7a42' : '#ffffff',
          color: flash ? '#ffffff' : '#6b7280',
          fontSize: estMobile ? 15 : 12, fontWeight: 700, lineHeight: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 1px 2px rgba(16,24,40,.06)', transition: 'background .18s, color .18s, border-color .18s',
          opacity: bloque ? .4 : 1,
        }}
      >{flash ? '✓' : '+'}</button>
      {flash && (
        <div style={{
          position: 'absolute', top: estMobile ? 32 : 24, right: 0, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 6,
          background: '#111827', color: '#fff', borderRadius: 6, padding: '3px 7px', fontSize: 10, fontWeight: 600,
        }}>Ajoute a {plan.pages.find((p) => p.id === plan.pageActive)?.nom}</div>
      )}
    </>
  );

  if (enFlux) return <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>{contenu}</span>;
  // Ecart calque sur la taille du bouton voisin ("i") : agrandis pour le
  // tactile, les deux se chevaucheraient a l'ecartement du bureau.
  const parDefaut = { top: 2, right: estMobile ? 36 : 26 };
  return <div style={{ position: 'absolute', ...(position || parDefaut), zIndex: 5 }}>{contenu}</div>;
}

/* --- Pastille d'acces permanente, en bas a droite ------------------------- */
function Lanceur() {
  const { cartes, notes, ouvert, setOuvert, P, envois } = usePlanDeTravail();
  const { estMobile } = useEcran();
  if (ouvert) return null;
  const total = cartes.length + notes.length;
  const nonLus = envois.filter((e) => !e.lu).length;
  return (
    <button onClick={() => setOuvert(true)} title="Ouvrir le plan de travail"
      style={{
        position: 'fixed', right: estMobile ? 12 : 20, bottom: estMobile ? 12 : 20, zIndex: 900, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 9,
        // Hauteur de cible confortable au doigt, et libelle abrege pour ne pas
        // barrer l'ecran d'un telephone.
        padding: estMobile ? '12px 16px' : '10px 15px', minHeight: estMobile ? 44 : 0,
        background: P.panneau, color: P.texte, border: `1px solid ${total ? P.bordFort : P.bord}`,
        borderRadius: 30, boxShadow: P.ombre, fontFamily: MONO, fontSize: 11.5, letterSpacing: '.06em',
        backdropFilter: 'blur(6px)',
      }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%', background: total ? P.accent : P.faible,
        boxShadow: total ? `0 0 8px ${P.accent}` : 'none', flexShrink: 0,
      }} />
      {estMobile ? 'PLAN' : 'PLAN DE TRAVAIL'}
      <span style={{
        background: total ? P.accent : P.accentSourd, color: total ? P.panneau : P.faible,
        borderRadius: 20, minWidth: 20, padding: '1px 6px', fontWeight: 700, textAlign: 'center',
      }}>{total}</span>
      {/* Une page recue d'un collegue doit se voir sans ouvrir le plan. */}
      {nonLus > 0 && (
        <span title={`${nonLus} page(s) recue(s) non lue(s)`} style={{
          background: P.danger, color: '#fff', borderRadius: 20, minWidth: 20,
          padding: '1px 6px', fontWeight: 700, textAlign: 'center',
        }}>✉ {nonLus}</span>
      )}
    </button>
  );
}

/* --- La surimpression : page floutee derriere, elements flottants devant -- */
function Surimpression({ registre, comparateur }) {
  const {
    cartes, setOuvert, majCarte, auPremierPlan, retirer, mode, P,
    notes, majNote, retirerNote, noteAuPremierPlan, verrouillee,
  } = usePlanDeTravail();
  const { estCompact, estMobile } = useEcran();
  const toile = useRef(null);
  const [glisse, setGlisse] = useState(null);
  /* Position vivante pendant le deplacement, gardee HORS de l'etat partage.
     Ecrire dans les pages a chaque mousemove reserialisait tous les
     instantanes vers localStorage a chaque pixel parcouru. */
  const [posVive, setPosVive] = useState(null);
  /* Miroir de cette position. Indispensable pour la consigner au relachement :
     lire l'etat depuis un updater `setPosVive(p => ...)` et y declencher une
     mise a jour reviendrait a modifier le provider pendant un rendu. */
  const posViveRef = useRef(null);
  const [imprime, setImprime] = useState(false);

  // Echap ferme, et on bloque le defilement de la page derriere.
  useEffect(() => {
    const surTouche = (e) => { if (e.key === 'Escape') setOuvert(false); };
    window.addEventListener('keydown', surTouche);
    const avant = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', surTouche); document.body.style.overflow = avant; };
  }, [setOuvert]);

  /* --- Impression / export PDF -------------------------------------------
     On passe par la boite d'impression du navigateur plutot que par une
     bibliotheque de capture : « Enregistrer au format PDF » y est offert
     partout, y compris sur telephone, le texte reste selectionnable, et cela
     n'ajoute aucune dependance.

     Le temps de l'impression, la page adopte la mise en forme d'un document :
     palette claire (l'encre sombre sur papier est illisible et ruine une
     cartouche) et elements empiles, car un plan libre ne se decoupe pas en
     feuilles A4. */
  useEffect(() => {
    const apres = () => setImprime(false);
    window.addEventListener('afterprint', apres);
    return () => window.removeEventListener('afterprint', apres);
  }, []);

  const imprimer = () => {
    setImprime(true);
    /* Laisser le temps a la disposition document de se peindre AVANT d'ouvrir
       la boite d'impression. Deux etapes s'enchainent : React remonte les
       graphes dans le portail, puis chacun mesure sa largeur reelle et se
       redessine. Ouvrir trop tot capturait des graphes vides.

       L'animation d'apparition de recharts est par ailleurs coupee en mode
       fige : elle part de zero (barres a hauteur nulle, secteurs a angle nul)
       et aucun delai raisonnable n'aurait suffi a l'attendre. */
    setTimeout(() => window.print(), 400);
  };

  /* --- Mesure des elements ------------------------------------------------
     La largeur est connue (LARGEUR / LARGEUR_NOTE) mais PAS la hauteur : un
     element epingle fait exactement la taille de son contenu. Or sans hauteur,
     impossible d'aligner sur un bord bas ou un centre vertical, ni de placer
     un ecart entre deux voisins. On mesure donc le DOM. */
  const noeuds = useRef(new Map());
  const [tailles, setTailles] = useState({});

  const enregistrerNoeud = useCallback((clef, el) => {
    if (el) noeuds.current.set(clef, el);
    else noeuds.current.delete(clef);
  }, []);

  useEffect(() => {
    const mesurer = () => {
      const t = {};
      noeuds.current.forEach((el, clef) => {
        if (el && el.offsetHeight) t[clef] = { l: el.offsetWidth, h: el.offsetHeight };
      });
      setTailles((avant) => {
        // On ne re-rend que si une taille a reellement change : les graphes
        // se redessineraient sinon a chaque mesure.
        const clefs = new Set([...Object.keys(avant), ...Object.keys(t)]);
        for (const c of clefs) {
          if (avant[c]?.l !== t[c]?.l || avant[c]?.h !== t[c]?.h) return t;
        }
        return avant;
      });
    };
    mesurer();
    const t = setTimeout(mesurer, 350);   // apres le dessin des graphes
    return () => clearTimeout(t);
  }, [cartes, notes, estCompact]);

  /* Geometrie de chaque element, hors celui qu'on deplace : c'est la matiere
     des reperes d'alignement et du calcul de voisinage. */
  const geometries = useCallback((exclureClef) => {
    const liste = [];
    const ajouter = (el, type, largeurDefaut) => {
      const clef = `${type}:${el.id}`;
      if (clef === exclureClef) return;
      const m = tailles[clef];
      liste.push({
        clef, id: el.id, type, kind: el.kind,
        x: el.pos.x, y: el.pos.y,
        l: m?.l || largeurDefaut, h: m?.h || 200,
        el,
      });
    };
    cartes.forEach((c) => ajouter(c, 'carte', LARGEUR[c.taille]));
    notes.forEach((n) => ajouter(n, 'note', LARGEUR_NOTE[n.taille]));
    return liste;
  }, [cartes, notes, tailles]);

  /* Deplacement. On ecoute les evenements POINTEUR et non la souris : ils
     couvrent souris, doigt et stylet d'un seul jeu de gestionnaires. Ecrit en
     `mousemove`, rien ne bougeait sur un ecran tactile. */
  useEffect(() => {
    if (!glisse) return undefined;

    const autres = geometries(`${glisse.type}:${glisse.id}`);
    const moi = tailles[`${glisse.type}:${glisse.id}`];
    const largeurMoi = moi?.l || glisse.largeur || 316;
    const hauteurMoi = moi?.h || 200;

    const surDeplacement = (e) => {
      const r = toile.current?.getBoundingClientRect();
      if (!r) return;
      const brutX = Math.max(0, e.clientX - r.left + toile.current.scrollLeft - glisse.dx);
      const brutY = Math.max(0, e.clientY - r.top + toile.current.scrollTop - glisse.dy);
      const { x, y, reperes } = aimanter(brutX, brutY, largeurMoi, hauteurMoi, autres);
      const p = { id: glisse.id, type: glisse.type, x, y, reperes };
      posViveRef.current = p;
      setPosVive(p);
    };
    const surRelache = () => {
      const p = posViveRef.current;
      if (p && p.id === glisse.id) {
        const consigner = glisse.type === 'note' ? majNote : majCarte;
        consigner(glisse.id, { pos: { x: p.x, y: p.y } });
      }
      posViveRef.current = null;
      setPosVive(null);
      setGlisse(null);
    };
    window.addEventListener('pointermove', surDeplacement);
    window.addEventListener('pointerup', surRelache);
    // Un appui interrompu (notification, appel entrant) emet pointercancel et
    // non pointerup : sans cela l'element resterait colle au doigt.
    window.addEventListener('pointercancel', surRelache);
    return () => {
      window.removeEventListener('pointermove', surDeplacement);
      window.removeEventListener('pointerup', surRelache);
      window.removeEventListener('pointercancel', surRelache);
    };
  }, [glisse, majCarte, majNote, geometries, tailles]);

  // Graphes et notes partagent la meme mecanique de position : `type` sert
  // seulement a savoir dans quelle liste consigner le resultat.
  const posDe = (el, type) => (posVive && posVive.id === el.id && posVive.type === type ? posVive : el.pos);

  const debutGlisse = useCallback((e, el, pos, type) => {
    // Sur une page verrouillee, la mutation serait de toute facon refusee :
    // autant ne pas laisser l'element suivre le curseur pour revenir en place
    // au relachement, ce qui donnerait l'impression d'un defaut.
    if (verrouillee) return;
    const r = toile.current?.getBoundingClientRect();
    if (!r) return;
    setGlisse({
      id: el.id,
      type,
      dx: e.clientX - (r.left - toile.current.scrollLeft + pos.x),
      dy: e.clientY - (r.top - toile.current.scrollTop + pos.y),
    });
  }, [verrouillee]);

  // La toile s'etend au-dela des elements pour laisser de la place autour.
  const largeurToile = Math.max(1100,
    ...cartes.map((c) => posDe(c, 'carte').x + LARGEUR[c.taille] + 60),
    ...notes.map((n) => posDe(n, 'note').x + LARGEUR_NOTE[n.taille] + 60));
  const hauteurToile = Math.max(600,
    ...cartes.map((c) => posDe(c, 'carte').y + HAUTEUR[c.taille] + 60),
    ...notes.map((n) => posDe(n, 'note').y + HAUTEUR_NOTE[n.taille] + 60));

  // A l'impression, on force la palette claire et la disposition empilee.
  const modeRendu = imprime ? 'clair' : mode;
  const Pr = imprime ? PALETTES.clair : P;

  /* La vue d'impression est projetee DIRECTEMENT SOUS `body`, par un portail.

     C'est indispensable : la feuille de style d'impression masque tout ce qui
     n'est pas le document, et elle ne peut le faire qu'en designant des
     enfants directs de `body`. Rendue a sa place naturelle — profondement
     imbriquee dans le tableau de bord — la vue etait masquee EN MEME TEMPS que
     son conteneur, et le PDF sortait vide.

     Un portail donne aussi la bonne pagination : l'element est dans le flux
     normal, donc le contenu se repartit sur plusieurs feuilles au lieu d'etre
     tronque a la premiere. */
  if (imprime) {
    return createPortal(
      // Largeur calee sur une A4 utile (210 mm moins 2 x 12 mm de marge, soit
      // environ 700 px a 96 ppp). Sans cette contrainte, les graphes se
      // mesureraient a la largeur de l'ECRAN — souvent 1200 px et plus — et
      // sortiraient tronques a droite sur le papier.
      <div className="plan-impression" style={{ width: 680, maxWidth: '100%', background: '#ffffff', fontFamily: MONO }}>
        <EnteteImpression P={Pr} />

        {/* Les ecarts d'abord : sur un document de reunion, c'est la
            conclusion qu'on cherche, pas les graphes qui l'etayent. Ce sont
            EXACTEMENT ceux affiches a l'ecran — meme detection de voisinage —
            sans quoi le rapport ne correspondrait pas au travail. */}
        <TableauEcarts cartes={cartes} tailles={tailles} comparateur={comparateur} P={Pr} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {notes.map((n) => (
            <Note key={n.id} n={n} P={Pr} empile imprime onMaj={majNote} onRetirer={retirerNote} />
          ))}
          {cartes.map((c) => (
            <Carte key={c.id} c={c} registre={registre} mode={modeRendu} P={Pr} empile imprime onRetirer={retirer} />
          ))}
          {!cartes.length && !notes.length && (
            <div style={{ color: Pr.faible, fontSize: 12 }}>Cette page ne contient aucun element.</div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 950,
      background: Pr.voile,
      backdropFilter: 'blur(9px) saturate(.75)',
      WebkitBackdropFilter: 'blur(9px) saturate(.75)',
      display: 'flex', flexDirection: 'column', fontFamily: MONO,
      userSelect: glisse ? 'none' : 'auto',
    }}>
      <BarreOutils onFermer={() => setOuvert(false)} onImprimer={imprimer} />

      {/* Sur telephone et petite tablette, on renonce au plan libre : les
          elements s'empilent sur toute la largeur, dans l'ordre ou ils ont ete
          ajoutes. Positionner des blocs a la main sur 400 pixels de large n'a
          aucun interet, et le resultat serait illisible. */}
      {estCompact ? (
        <div style={{ flex: 1, overflow: 'auto', padding: estMobile ? '12px 10px 28px' : '16px 18px 32px' }}>
          {!cartes.length && !notes.length ? <EtatVide P={Pr} empile /> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 640, margin: '0 auto' }}>
              {notes.map((n) => (
                <Note key={n.id} n={n} P={Pr} empile fige={verrouillee} onMaj={majNote} onRetirer={retirerNote} />
              ))}
              {cartes.map((c) => (
                <Carte key={c.id} c={c} registre={registre} mode={modeRendu} P={Pr} empile fige={verrouillee} onRetirer={retirer} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div ref={toile} style={{
          position: 'relative', flex: 1, overflow: 'auto',
          // Quadrillage tres attenue : les graphes reposant directement dessus,
          // il doit se deviner sans jamais concurrencer une courbe.
          backgroundImage: `linear-gradient(${P.grille} 1px, transparent 1px), linear-gradient(90deg, ${P.grille} 1px, transparent 1px)`,
          backgroundSize: '46px 46px',
        }}>
          <div style={{ position: 'relative', width: largeurToile, height: hauteurToile }}>
            {!cartes.length && !notes.length && <EtatVide P={P} />}

            {/* Ecarts entre voisins de meme nature : places sous les elements
                pour ne jamais gener un clic. */}
            <Comparaisons cartes={cartes} tailles={tailles} posDe={posDe} comparateur={comparateur} P={P} />

            {cartes.map((c) => (
              <Carte key={c.id} c={c} pos={posDe(c, 'carte')} registre={registre} mode={mode} P={P} fige={verrouillee}
                refMesure={(el) => enregistrerNoeud(`carte:${c.id}`, el)}
                onDebutGlisse={debutGlisse}
                onPremierPlan={auPremierPlan}
                onTaille={majCarte}
                onRetirer={retirer}
                actif={glisse?.type === 'carte' && glisse?.id === c.id} />
            ))}
            {notes.map((n) => (
              <Note key={n.id} n={n} pos={posDe(n, 'note')} P={P} fige={verrouillee}
                refMesure={(el) => enregistrerNoeud(`note:${n.id}`, el)}
                onDebutGlisse={debutGlisse}
                onPremierPlan={noteAuPremierPlan}
                onMaj={majNote}
                onRetirer={retirerNote}
                actif={glisse?.type === 'note' && glisse?.id === n.id} />
            ))}

            {/* Reperes d'alignement, au-dessus de tout : ils doivent rester
                visibles meme par-dessus l'element deplace. */}
            {(posVive?.reperes || []).map((g, i) => (
              <div key={i} style={{
                position: 'absolute', zIndex: 9999, pointerEvents: 'none',
                background: P.accent, boxShadow: `0 0 4px ${P.accent}`,
                ...(g.axe === 'x'
                  ? { left: g.position, top: g.de - 12, width: 1, height: (g.a - g.de) + 24 }
                  : { top: g.position, left: g.de - 12, height: 1, width: (g.a - g.de) + 24 }),
              }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* --- Ecarts entre elements voisins ----------------------------------------
   Deux graphes de meme nature poses cote a cote invitent a la comparaison :
   autant la faire. Le pourcentage affiche entre eux est celui de l'element de
   DROITE par rapport a celui de GAUCHE — le sens de lecture.

   Ne sont rapproches que des elements reellement voisins : meme type, memes
   grandeurs, sur la meme rangee et separes d'au plus une gouttiere large. On
   ne compare pas deux blocs qui se trouvent au meme endroit par hasard. */
const ECART_MAX_VOISINS = 140;

/* L'ECART EN VALEUR rend l'asymetrie du pourcentage evidente. Entre 4 978 DT
   et 5 785 DT, la difference vaut 807 DT dans les DEUX sens de lecture ; seul
   le pourcentage change, selon qu'on la rapporte a l'un ou a l'autre — 16 %
   ou 14 %. N'afficher que le pourcentage donnait l'impression d'une
   incoherence a chaque fois qu'on intervertissait deux elements. */
function ecartEnClair(v, unite) {
  const abs = Math.abs(v);
  // Sous 100, la decimale porte une information (un panier moyen, un prix) ;
  // au-dela elle n'apporte rien a un chiffre d'affaires.
  const nb = abs < 100 ? Math.round(abs * 10) / 10 : Math.round(abs);
  return `${v >= 0 ? '+' : '−'}${nb.toLocaleString('fr-FR')}${unite ? ` ${unite}` : ''}`;
}

/* Detection des paires, partagee par l'affichage a l'ecran et le rapport PDF :
   les ecarts imprimes doivent etre EXACTEMENT ceux qu'on a vus en composant la
   page, sans quoi le document ne correspondrait pas au travail. */
export function pairesVoisines(cartes, tailles, comparateur, position) {
  if (!comparateur) return [];
  const pos = position || ((c) => c.pos);

  const blocs = cartes.map((c) => {
    const m = tailles[`carte:${c.id}`];
    const p = pos(c);
    const v = comparateur(c.kind, c.data, c);
    return v ? { c, x: p.x, y: p.y, l: m?.l || LARGEUR[c.taille], h: m?.h || 200, v } : null;
  }).filter(Boolean);

  /* Deux elements ne sont rapproches que s'ils mesurent LA MEME CHOSE. Trois
     conditions, chacune ajoutee apres avoir vu l'ecart qu'elle evite :

     - meme nature de graphe et meme grandeur : on ne rapproche pas un chiffre
       d'affaires d'un nombre de visiteurs ;
     - meme portee : reseau avec reseau, boutique avec boutique. Comparer le
       reseau a l'une de ses boutiques donnait « -79,8 % », qui ne mesurait
       aucune baisse mais la part de cette boutique ;
     - duree COMPARABLE : deux jours cumules face a un seul jour donnaient
       « -71 % », qui ne mesurait aucune evolution mais le fait qu'une periode
       est deux fois plus longue.

     La duree tolere un ecart de 15 % plutot que d'exiger l'egalite stricte :
     fevrier compte 28 jours et mars 31, or comparer deux mois est un usage
     legitime et courant. En revanche un jour face a deux (rapport 2) reste
     ecarte. */
  const dureeCompatible = (a, b) => {
    const j1 = a.v.jours || 1;
    const j2 = b.v.jours || 1;
    return Math.max(j1, j2) / Math.min(j1, j2) <= 1.15;
  };

  const memeSujet = (a, b) => a.c.kind === b.c.kind
    && a.v.unite === b.v.unite
    && a.v.perimetre === b.v.perimetre
    && dureeCompatible(a, b);

  const paires = [];
  blocs.forEach((g) => {
    // Le voisin de droite le plus proche qui parle bien de la meme chose.
    const d = blocs
      .filter((o) => o !== g && memeSujet(g, o) && o.x > g.x)
      .map((o) => ({ o, ecart: o.x - (g.x + g.l) }))
      .filter(({ o, ecart }) => ecart >= -20 && ecart <= ECART_MAX_VOISINS
        && Math.abs(o.y - g.y) < Math.max(60, g.h * 0.4))
      .sort((a, b) => a.ecart - b.ecart)[0];
    if (!d || !g.v.valeur) return;   // pas d'evolution calculable a partir de zero

    // Rien a apprendre de deux instantanes strictement identiques : meme
    // periode, meme perimetre, donc 0 % par construction.
    const memeBoutique = (g.v.boutique || '') === (d.o.v.boutique || '');
    const memePeriode = g.v.periode === d.o.v.periode;
    if (memeBoutique && memePeriode) return;

    const ecart = d.o.v.valeur - g.v.valeur;
    const variation = (ecart / Math.abs(g.v.valeur)) * 100;
    paires.push({
      g, d: d.o, variation, ecart,
      // Sous 10 %, la decimale porte une information ; au-dela elle encombre.
      arrondi: Math.abs(variation) >= 10 ? Math.round(variation) : Math.round(variation * 10) / 10,
      hausse: variation >= 0,
      // Ce qui distingue les deux : le dire evite de faire deviner si l'ecart
      // porte sur le temps ou sur la boutique.
      difference: !memeBoutique
        ? (d.o.v.boutique || 'Reseau')
        : (d.o.v.periode.split('·')[0] || '').trim(),
    });
  });
  return paires;
}

function Comparaisons({ cartes, tailles, posDe, comparateur, P }) {
  const paires = pairesVoisines(cartes, tailles, comparateur, (c) => posDe(c, 'carte'));

  return paires.map(({ g, d, arrondi, hausse, difference, ecart }) => {
    const centreX = (g.x + g.l + d.x) / 2;
    const centreY = Math.min(g.y, d.y) + Math.min(g.h, d.h) / 2;
    return (
      <div key={`${g.c.id}-${d.c.id}`}
        title={[
          d.v.libelle,
          `${g.v.periode} (${g.v.jours} j) : ${g.v.texte}`,
          `${d.v.periode} (${d.v.jours} j) : ${d.v.texte}`,
          '',
          // Un pourcentage se rapporte toujours a son point de depart :
          // inverser les deux elements ne donne pas l'oppose. Montrer le calcul
          // vaut mieux que de l'affirmer.
          `Ecart : ${ecartEnClair(ecart, g.v.uniteAffichee)}`,
          `soit ${ecartEnClair(ecart, g.v.uniteAffichee)} rapporte a ${g.v.texte} = ${arrondi > 0 ? '+' : ''}${arrondi} %`,
          `(inverser les deux elements change le point de reference,`,
          ` donc le pourcentage — l'ecart, lui, reste le meme)`,
        ].join('\n')}
        style={{
          position: 'absolute', left: centreX, top: centreY, transform: 'translate(-50%,-50%)',
          // Au-dessus des elements : une gouttiere fait 24 px et la pastille
          // en fait 58, elle serait invisible en dessous. `pointerEvents:
          // none` garantit qu'elle n'intercepte jamais un clic ni un
          // deplacement, malgre ce recouvrement.
          zIndex: 9998, pointerEvents: 'none', textAlign: 'center',
          background: P.panneau, border: `1px solid ${hausse ? '#4ad98a' : '#ff7a6b'}`,
          borderRadius: 20, padding: '5px 9px', minWidth: 58,
          boxShadow: P.ombre,
        }}>
        <div style={{ color: hausse ? '#4ad98a' : '#ff7a6b', fontSize: 12.5, fontWeight: 700, lineHeight: 1.1 }}>
          {hausse ? '▲' : '▼'} {Math.abs(arrondi)}%
        </div>
        {/* L'ecart en valeur : identique dans les deux sens de lecture, il
            explique a lui seul pourquoi le pourcentage, lui, differe. */}
        <div style={{ color: P.texte, fontSize: 9.5, fontWeight: 600, marginTop: 1, whiteSpace: 'nowrap' }}>
          {ecartEnClair(ecart, g.v.uniteAffichee)}
        </div>
        {/* Ce qui distingue les deux elements : sans cette mention, on ne sait
            pas si l'ecart porte sur la periode ou sur la boutique. */}
        <div style={{
          color: P.faible, fontSize: 8.5, letterSpacing: '.03em', marginTop: 1,
          maxWidth: 118, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          vs {difference}
        </div>
      </div>
    );
  });
}

/* --- Barre d'outils : pages, mode d'affichage, actions ------------------- */
function BarreOutils({ onFermer, onImprimer }) {
  const {
    cartes, vider, P, mode, basculerMode, echecStockage, synchro, ajouterNote, envois,
    pages, pageActive, ajouterPage, choisirPage, renommerPage, supprimerPage,
    basculerVerrou, dupliquerPage, verrouillee, peutRejouer,
  } = usePlanDeTravail();
  const { estMobile } = useEcran();
  const [renomme, setRenomme] = useState(null);
  const [panneau, setPanneau] = useState(null);   // 'envoi' | 'recus' | null
  const nonLus = envois.filter((e) => !e.lu).length;

  const bouton = (label, onClick, titre, danger) => (
    <button onClick={onClick} title={titre} style={{
      background: 'transparent', color: danger ? P.danger : P.texte,
      border: `1px solid ${danger ? P.danger : P.bord}`, borderRadius: 7,
      // 44 pixels de haut au doigt : c'est la cible minimale tenable.
      padding: estMobile ? '10px 12px' : '6px 11px', minHeight: estMobile ? 40 : 0,
      fontSize: estMobile ? 10.5 : 11, fontFamily: MONO, letterSpacing: '.05em', cursor: 'pointer',
      opacity: danger ? .85 : 1, whiteSpace: 'nowrap',
    }}>{label}</button>
  );

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: estMobile ? 7 : 10,
      padding: estMobile ? '9px 10px' : '10px 16px', flexWrap: 'wrap',
      borderBottom: `1px solid ${P.bord}`, background: P.panneau, flexShrink: 0,
      // Une barre chargee ne doit jamais manger toute la hauteur d'un telephone.
      maxHeight: estMobile ? '42vh' : 'none', overflowY: estMobile ? 'auto' : 'visible',
    }}>
      {!estMobile && <span style={{ width: 8, height: 8, borderRadius: '50%', background: P.accent, boxShadow: `0 0 10px ${P.accent}`, flexShrink: 0 }} />}

      {/* Onglets de pages : un plan par sujet de reunion. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        {pages.map((p) => {
          const actif = p.id === pageActive;
          if (renomme === p.id) {
            return (
              <input key={p.id} autoFocus defaultValue={p.nom} maxLength={40}
                placeholder="Nom de la page"
                onFocus={(e) => e.target.select()}
                onBlur={(e) => { renommerPage(p.id, e.target.value); setRenomme(null); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { renommerPage(p.id, e.target.value); setRenomme(null); }
                  if (e.key === 'Escape') setRenomme(null);
                }}
                style={{
                  width: 150, background: P.surface, color: P.texte, border: `1px solid ${P.bordFort}`,
                  borderRadius: 7, padding: '5px 9px', fontSize: 11.5, fontFamily: MONO,
                }} />
            );
          }
          // Une page recue d'un collegue se signale au premier coup d'oeil :
          // nom de l'expediteur, en rouge. On ne doit pas la confondre avec
          // son propre travail.
          const recue = !!p.expediteur;
          return (
            <button key={p.id} onClick={() => choisirPage(p.id)} onDoubleClick={() => setRenomme(p.id)}
              title={recue
                ? `Recue de ${p.expediteur} — clic pour ouvrir, double-clic pour renommer`
                : 'Clic pour ouvrir, double-clic pour renommer'}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                background: actif ? (recue ? 'rgba(255,122,107,.12)' : P.accentSourd) : 'transparent',
                color: recue ? P.danger : (actif ? P.accent : P.faible),
                border: `1px solid ${recue ? P.danger : (actif ? P.bordFort : P.bord)}`,
                borderRadius: 7, padding: '5px 10px', fontSize: 11.5, fontFamily: MONO, letterSpacing: '.03em',
              }}>
              {recue && <span style={{ fontSize: 9, opacity: .9 }}>✉</span>}
              {p.verrouillee && <span title="Page verrouillee" style={{ fontSize: 9 }}>🔒</span>}
              {recue ? `${p.expediteur} · ${p.nom}` : p.nom}
              {/* Graphes et notes confondus : c'est le remplissage de la page
                  qui interesse, pas la ventilation par type. */}
              <span style={{ opacity: .65, fontSize: 10 }}>{p.cartes.length + (p.notes?.length || 0)}</span>
              {/* Renommer et supprimer ne s'affichent que sur la page ouverte,
                  pour ne pas alourdir la rangee d'onglets. */}
              {actif && (
                <span role="button" tabIndex={0} title="Renommer cette page"
                  onClick={(e) => { e.stopPropagation(); setRenomme(p.id); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setRenomme(p.id); } }}
                  style={{ color: P.accent, fontSize: 10, lineHeight: 1, cursor: 'pointer', opacity: .8 }}>✎</span>
              )}
              {actif && pages.length > 1 && (
                <span role="button" tabIndex={0} title="Supprimer cette page"
                  onClick={(e) => { e.stopPropagation(); supprimerPage(p.id); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); supprimerPage(p.id); } }}
                  style={{ color: P.danger, fontSize: 11, lineHeight: 1, cursor: 'pointer' }}>✕</span>
              )}
            </button>
          );
        })}
        {pages.length < MAX_PAGES && (
          // On cree la page ET on ouvre son champ de nom dans la foulee.
          <button onClick={() => setRenomme(ajouterPage())} title="Nouvelle page"
            style={{
              cursor: 'pointer', background: 'transparent', color: P.faible,
              border: `1px dashed ${P.bord}`, borderRadius: 7, padding: '5px 10px', fontSize: 12, fontFamily: MONO,
            }}>+ page</button>
        )}
      </div>

      {echecStockage ? (
        <span style={{
          color: P.danger, fontSize: 10.5, lineHeight: 1.4, maxWidth: 380,
          border: `1px solid ${P.danger}`, borderRadius: 7, padding: '4px 9px',
        }}>{echecStockage}</span>
      ) : (
        <EtatSynchro synchro={synchro} P={P} />
      )}

      <div style={{ flex: 1 }} />

      {/* Libelles abreges sur telephone : la barre doit tenir sur une ou deux
          lignes, pas sur cinq. */}
      {!verrouillee && bouton(estMobile ? '✎' : '✎ NOTE', ajouterNote, 'Ajouter une note sur cette page')}

      {bouton(verrouillee ? (estMobile ? '🔒' : '🔒 VERROUILLEE') : (estMobile ? '🔓' : '🔓 VERROUILLER'),
        () => basculerVerrou(pageActive),
        verrouillee
          ? 'Page figee : deverrouiller pour la modifier a nouveau'
          : 'Figer cette page : plus aucun element ne pourra etre deplace, ajoute ni supprime')}

      {bouton(estMobile ? '⧉' : '⧉ DUPLIQUER', () => setRenomme(dupliquerPage(pageActive)),
        'Creer une copie a l\'identique de cette page — memes graphes, memes donnees')}

      {peutRejouer && cartes.length > 0 && bouton(estMobile ? '⟲' : '⟲ AUTRE PERIODE',
        () => setPanneau(panneau === 'rejeu' ? null : 'rejeu'),
        'Recreer cette page a l\'identique, mais avec les chiffres d\'une autre periode')}
      {(cartes.length > 0 || pages.find((p) => p.id === pageActive)?.notes?.length > 0) && (
        <>
          {bouton(estMobile ? '⎙' : '⎙ PDF', onImprimer,
            'Imprimer cette page ou l\'enregistrer en PDF (choisissez « Enregistrer au format PDF » comme imprimante)')}
          {bouton(estMobile ? '↗' : '↗ ENVOYER', () => setPanneau(panneau === 'envoi' ? null : 'envoi'),
            'Envoyer une copie de cette page a des collegues')}
        </>
      )}

      {/* Boite de reception : toujours accessible, meme sur une page vide. */}
      <button onClick={() => setPanneau(panneau === 'recus' ? null : 'recus')}
        title={nonLus ? `${nonLus} page(s) recue(s) non lue(s)` : 'Pages recues de vos collegues'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          background: nonLus ? P.danger : 'transparent', color: nonLus ? '#fff' : P.texte,
          border: `1px solid ${nonLus ? P.danger : P.bord}`, borderRadius: 7,
          padding: estMobile ? '10px 12px' : '6px 11px', minHeight: estMobile ? 40 : 0,
          fontSize: estMobile ? 10.5 : 11, fontFamily: MONO, letterSpacing: '.05em',
        }}>
        ✉{nonLus > 0 && <span style={{ fontWeight: 700 }}>{nonLus}</span>}
      </button>
      {bouton(mode === 'sombre' ? (estMobile ? '☀' : '☀ MODE CLAIR') : (estMobile ? '☾' : '☾ MODE NUIT'), basculerMode,
        'Basculer entre fond nuit et fond clair (pour projeter ou imprimer)')}
      {cartes.length > 0 && !verrouillee && bouton(estMobile ? '⌫' : 'VIDER LES GRAPHES', vider,
        'Retirer les graphes de cette page (les notes restent)', true)}
      {bouton(estMobile ? '✕' : 'FERMER  ESC', onFermer, 'Fermer le plan de travail')}

      {panneau === 'envoi' && <PanneauEnvoi P={P} onFermer={() => setPanneau(null)} />}
      {panneau === 'recus' && <PanneauRecus P={P} onFermer={() => setPanneau(null)} />}
      {panneau === 'rejeu' && <PanneauRejeu P={P} onFermer={() => setPanneau(null)} />}
    </div>
  );
}

/* --- Choix des destinataires ---------------------------------------------
   La liste vient des comptes de l'application : pas de lien a faire circuler,
   et l'expediteur sait exactement qui recoit. */
function PanneauEnvoi({ P, onFermer }) {
  const { chargerCollegues, envoyerPage } = usePlanDeTravail();
  const { estMobile } = useEcran();
  const [collegues, setCollegues] = useState(null);
  const [choisis, setChoisis] = useState([]);
  const [etat, setEtat] = useState(null);   // {envoi:true} | {succes:n} | {erreur}

  useEffect(() => {
    let annule = false;
    chargerCollegues()
      .then((l) => { if (!annule) setCollegues(l); })
      .catch((e) => { if (!annule) { setCollegues([]); setEtat({ erreur: e.message }); } });
    return () => { annule = true; };
  }, [chargerCollegues]);

  const basculer = (id) => setChoisis((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const envoyer = async () => {
    setEtat({ envoi: true });
    try {
      const n = await envoyerPage(choisis);
      setEtat({ succes: n });
      setChoisis([]);
      setTimeout(onFermer, 1800);
    } catch (e) {
      setEtat({ erreur: e.message });
    }
  };

  return (
    <div style={{
      width: '100%', background: P.surface, border: `1px solid ${P.bord}`,
      borderRadius: 8, padding: '10px 12px', marginTop: 2,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ color: P.accent, fontSize: 10.5, letterSpacing: '.08em', flex: 1 }}>
          ENVOYER CETTE PAGE A
        </span>
        <button onClick={onFermer} style={{ background: 'transparent', border: 'none', color: P.faible, cursor: 'pointer', fontSize: 13 }}>✕</button>
      </div>

      {collegues === null && <div style={{ color: P.faible, fontSize: 11 }}>Chargement des collegues…</div>}
      {collegues && collegues.length === 0 && (
        <div style={{ color: P.faible, fontSize: 11 }}>Aucun autre compte actif dans l'application.</div>
      )}

      {collegues && collegues.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 160, overflowY: 'auto' }}>
          {collegues.map((c) => {
            const actif = choisis.includes(c.id);
            return (
              <button key={c.id} onClick={() => basculer(c.id)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                background: actif ? P.accentSourd : 'transparent',
                color: actif ? P.accent : P.texte,
                border: `1px solid ${actif ? P.bordFort : P.bord}`, borderRadius: 20,
                padding: estMobile ? '9px 12px' : '6px 11px', fontSize: 11.5, fontFamily: MONO,
              }}>
                <span style={{ opacity: actif ? 1 : .3 }}>{actif ? '✓' : '○'}</span>
                {c.nom}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
        <button onClick={envoyer} disabled={!choisis.length || etat?.envoi} style={{
          cursor: choisis.length ? 'pointer' : 'not-allowed',
          background: choisis.length ? P.accent : 'transparent',
          color: choisis.length ? P.panneau : P.faible,
          border: `1px solid ${choisis.length ? P.accent : P.bord}`, borderRadius: 7,
          padding: estMobile ? '10px 14px' : '7px 14px', fontSize: 11, fontFamily: MONO,
          letterSpacing: '.06em', fontWeight: 700,
        }}>
          {etat?.envoi ? 'ENVOI…' : `ENVOYER${choisis.length ? ` (${choisis.length})` : ''}`}
        </button>
        {etat?.succes !== undefined && (
          <span style={{ color: P.accent, fontSize: 11 }}>
            Envoye a {etat.succes} collegue{etat.succes > 1 ? 's' : ''}.
          </span>
        )}
        {etat?.erreur && <span style={{ color: P.danger, fontSize: 11 }}>{etat.erreur}</span>}
        <span style={{ color: P.faible, fontSize: 10, flex: 1, minWidth: 160 }}>
          Copie figee : vos modifications suivantes ne changeront pas ce qu'ils recoivent.
        </span>
      </div>
    </div>
  );
}

/* --- Rejouer la page sur une autre periode --------------------------------
   Le resultat est une NOUVELLE page, l'originale restant intacte : c'est ce
   qui permet de poser les deux cote a cote et de laisser les ecarts se
   calculer d'eux-memes. */
function PanneauRejeu({ P, onFermer }) {
  const { rejouerSur, pageActive } = usePlanDeTravail();
  const { estMobile } = useEcran();
  const aujourdhui = new Date().toISOString().slice(0, 10);
  // Par defaut, le mois precedent en entier : c'est la comparaison la plus
  // demandee, et elle evite d'avoir a saisir deux dates.
  const moisPrecedent = () => {
    const d = new Date();
    const debut = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const fin = new Date(d.getFullYear(), d.getMonth(), 0);
    const iso = (x) => x.toISOString().slice(0, 10);
    return [iso(debut), iso(fin)];
  };
  const [d1, d2] = moisPrecedent();
  const [du, setDu] = useState(d1);
  const [au, setAu] = useState(d2);
  const [etat, setEtat] = useState(null);   // {encours} | {ok} | {erreur}

  const lancer = async () => {
    setEtat({ encours: true });
    try {
      const r = await rejouerSur(pageActive, du > au ? au : du, du > au ? du : au);
      setEtat({ ok: r });
      setTimeout(onFermer, 2600);
    } catch (e) {
      setEtat({ erreur: e.message });
    }
  };

  const champ = (valeur, changer, libelle) => (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: P.faible, fontSize: 11 }}>
      {libelle}
      <input type="date" max={aujourdhui} value={valeur} onChange={(e) => changer(e.target.value)}
        style={{
          background: P.surface, color: P.texte, border: `1px solid ${P.bord}`, borderRadius: 6,
          padding: estMobile ? '7px 8px' : '5px 7px', fontSize: estMobile ? 16 : 11, fontFamily: MONO,
        }} />
    </label>
  );

  return (
    <div style={{
      width: '100%', background: P.surface, border: `1px solid ${P.bord}`,
      borderRadius: 8, padding: '10px 12px', marginTop: 2,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ color: P.accent, fontSize: 10.5, letterSpacing: '.08em', flex: 1 }}>
          REJOUER CETTE PAGE SUR UNE AUTRE PERIODE
        </span>
        <button onClick={onFermer} style={{ background: 'transparent', border: 'none', color: P.faible, cursor: 'pointer', fontSize: 13 }}>✕</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {champ(du, setDu, 'du')}
        {champ(au, setAu, 'au')}
        <button onClick={lancer} disabled={etat?.encours} style={{
          cursor: 'pointer', background: P.accent, color: P.panneau,
          border: `1px solid ${P.accent}`, borderRadius: 7, fontWeight: 700,
          padding: estMobile ? '10px 14px' : '7px 14px', fontSize: 11, fontFamily: MONO, letterSpacing: '.06em',
        }}>{etat?.encours ? 'REJEU…' : 'REJOUER'}</button>

        {etat?.ok && (
          <span style={{ color: P.accent, fontSize: 11 }}>
            {etat.ok.rejoues} element(s) rejoue(s) sur {etat.ok.libelle}
            {etat.ok.conserves > 0 && ` · ${etat.ok.conserves} conserve(s) tel(s) quel(s)`}
          </span>
        )}
        {etat?.erreur && <span style={{ color: P.danger, fontSize: 11 }}>{etat.erreur}</span>}
      </div>

      <div style={{ color: P.faible, fontSize: 10, marginTop: 8, lineHeight: 1.5 }}>
        Une nouvelle page est creee, meme composition mais chiffres de la periode choisie.
        L'originale est conservee : posez-les cote a cote pour que les ecarts se calculent.
        Un element dont la boutique n'existe pas sur la nouvelle periode garde ses donnees
        d'origine plutot que d'afficher un chiffre faux.
      </div>
    </div>
  );
}

/* --- Pages recues ---------------------------------------------------------
   Un envoi reste ici meme apres que la page a ete retiree du plan : c'est la
   notification qui fait foi, et elle permet de rouvrir a tout moment. */
function PanneauRecus({ P, onFermer }) {
  const { envois, ouvrirEnvoi, ecarterEnvoi, rafraichirEnvois } = usePlanDeTravail();
  const { estMobile } = useEcran();
  const [erreur, setErreur] = useState(null);
  const [sourdine, setSourdine] = useState(sourdineActive);

  useEffect(() => { rafraichirEnvois(); }, [rafraichirEnvois]);

  const changerSourdine = () => {
    const nouvelle = basculerSourdine();
    setSourdine(nouvelle);
    // On fait entendre le son en le reactivant : sans retour, on ne sait pas
    // si le reglage a pris, ni a quoi ressemble l'alerte.
    if (!nouvelle) jouerSonNotification();
  };

  const ouvrir = async (id) => {
    setErreur(null);
    try { await ouvrirEnvoi(id); onFermer(); } catch (e) { setErreur(e.message); }
  };

  return (
    <div style={{
      width: '100%', background: P.surface, border: `1px solid ${P.bord}`,
      borderRadius: 8, padding: '10px 12px', marginTop: 2,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ color: P.accent, fontSize: 10.5, letterSpacing: '.08em', flex: 1 }}>
          PAGES RECUES
        </span>
        <button onClick={changerSourdine}
          title={sourdine ? 'Signal sonore desactive — cliquer pour le retablir' : 'Signal sonore actif — cliquer pour le couper'}
          style={{
            background: 'transparent', border: `1px solid ${P.bord}`, borderRadius: 6,
            color: sourdine ? P.faible : P.accent, cursor: 'pointer',
            fontSize: 11, padding: estMobile ? '7px 9px' : '4px 8px', fontFamily: MONO,
          }}>{sourdine ? '🔇' : '🔔'}</button>
        <button onClick={onFermer} style={{ background: 'transparent', border: 'none', color: P.faible, cursor: 'pointer', fontSize: 13 }}>✕</button>
      </div>

      {erreur && <div style={{ color: P.danger, fontSize: 11, marginBottom: 8 }}>{erreur}</div>}

      {!envois.length && (
        <div style={{ color: P.faible, fontSize: 11 }}>
          Aucune page recue. Un collegue peut vous en envoyer une depuis son plan de travail.
        </div>
      )}

      <div style={{ display: 'grid', gap: 5, maxHeight: 220, overflowY: 'auto' }}>
        {envois.map((e) => (
          <div key={e.id} style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            background: e.lu ? 'transparent' : P.accentSourd,
            border: `1px solid ${e.lu ? P.bord : P.bordFort}`, borderRadius: 7,
            padding: estMobile ? '9px 10px' : '7px 10px',
          }}>
            <button onClick={() => ouvrir(e.id)} title="Ouvrir cette page dans mon plan de travail"
              style={{
                flex: 1, minWidth: 150, textAlign: 'left', cursor: 'pointer',
                background: 'transparent', border: 'none', padding: 0, fontFamily: MONO,
              }}>
              <div style={{ color: P.danger, fontSize: 11, fontWeight: 700 }}>
                {!e.lu && '● '}{e.expediteur}
              </div>
              <div style={{ color: P.texte, fontSize: 12 }}>{e.titre}</div>
              <div style={{ color: P.faible, fontSize: 10, marginTop: 2 }}>
                {new Date(e.envoye_le).toLocaleString('fr-FR', {
                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </div>
            </button>
            <button onClick={() => ecarterEnvoi(e.id)} title="Retirer definitivement cette page recue"
              style={{
                background: 'transparent', border: 'none', color: P.faible, cursor: 'pointer',
                fontSize: 13, padding: estMobile ? 8 : 4,
              }}>✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* L'utilisateur doit savoir si son travail est reellement a l'abri : "hors
   ligne" veut dire que le plan ne vit que dans ce navigateur et ne sera pas
   retrouve depuis un autre poste. */
function EtatSynchro({ synchro, P }) {
  const { estMobile } = useEcran();
  const etats = {
    chargement: { texte: 'chargement du plan…', couleur: P.faible, puce: '◌' },
    envoi: { texte: 'enregistrement…', couleur: P.faible, puce: '◍' },
    ok: { texte: 'enregistre sur le serveur', couleur: P.accent, puce: '●' },
    'hors-ligne': { texte: 'hors ligne — copie locale seulement', couleur: P.danger, puce: '○' },
  };
  const e = etats[synchro] || etats.ok;
  return (
    <span title={synchro === 'hors-ligne'
      ? "Le serveur n'a pas repondu : ce plan reste dans ce navigateur et ne sera pas visible depuis un autre poste."
      : 'Le plan est range sur le serveur, sous votre compte : vous le retrouverez depuis n\'importe quel poste.'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, color: e.couleur,
        fontSize: 10.5, letterSpacing: '.04em',
        /* LARGEUR RESERVEE. Ce libelle passe de « enregistrement… » a
           « enregistre sur le serveur » a chaque sauvegarde, soit une dizaine
           de caracteres — environ 70 pixels. Pose dans une barre en
           `flexWrap`, ce va-et-vient suffisait a renvoyer un bouton a la ligne
           et a changer la hauteur de toute la barre : elle sautait a chaque
           fois qu'on lachait un graphe.

           La largeur retenue est celle du plus long des libelles qui
           ALTERNENT. « hors ligne… » est plus long encore, mais c'est un etat
           qui s'installe au lieu de clignoter : il peut deborder sans gener.

           Sur telephone, la barre defile dans sa propre hauteur et le plan ne
           se deplace pas a la main : rien a reserver. */
        minWidth: estMobile ? 0 : 186,
        whiteSpace: 'nowrap', flexShrink: 0,
      }}>
      <span>{e.puce}</span>{e.texte}
    </span>
  );
}

/* En-tete du document imprime : sans lui, une feuille sortie de l'imprimante
   ne dit ni de quelle page il s'agit, ni de quand elle date. */
function EnteteImpression({ P }) {
  const { pages, pageActive } = usePlanDeTravail();
  const page = pages.find((p) => p.id === pageActive);
  const maintenant = new Date().toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16,
      borderBottom: `2px solid ${P.accent}`, paddingBottom: 8, marginBottom: 18,
    }}>
      <div>
        <div style={{ fontSize: 9.5, letterSpacing: '.18em', color: P.faible }}>PEAK RETAIL INTELLIGENCE</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: P.texte, marginTop: 2 }}>{page?.nom || 'Plan de travail'}</div>
      </div>
      <div style={{ fontSize: 10, color: P.faible, textAlign: 'right' }}>
        Edite le {maintenant}<br />
        {page ? `${page.cartes.length} graphe(s) · ${(page.notes || []).length} note(s)` : ''}
      </div>
    </div>
  );
}

/* --- Tableau des ecarts, en tete du rapport imprime -----------------------
   Une pastille posee entre deux graphes se lit bien a l'ecran ; sur papier,
   ou l'on empile les elements, elle n'aurait plus de sens. Les memes ecarts
   sont donc recapitules en toutes lettres, avec les deux valeurs comparees —
   c'est ce qu'on lit a voix haute en reunion. */
function TableauEcarts({ cartes, tailles, comparateur, P }) {
  const paires = pairesVoisines(cartes, tailles, comparateur);
  if (!paires.length) return null;

  const cellule = { padding: '6px 8px', borderTop: `1px solid ${P.bord}`, fontSize: 11, verticalAlign: 'top' };
  return (
    <div style={{ marginBottom: 22, breakInside: 'avoid', pageBreakInside: 'avoid' }}>
      <div style={{ color: P.accent, fontSize: 9.5, letterSpacing: '.14em', marginBottom: 4 }}>
        ECARTS RELEVES
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {paires.map(({ g, d, arrondi, hausse, ecart }) => (
            <tr key={`${g.c.id}-${d.c.id}`}>
              <td style={{ ...cellule, color: P.texte }}>
                <div style={{ fontWeight: 700 }}>{g.c.titre}</div>
                <div style={{ color: P.faible, fontSize: 9.5 }}>{g.c.periode}</div>
                <div style={{ color: P.faible, fontSize: 9.5 }}>{g.v.libelle} : {g.v.texte}</div>
              </td>
              <td style={{ ...cellule, color: P.faible, textAlign: 'center', width: 24 }}>→</td>
              <td style={{ ...cellule, color: P.texte }}>
                <div style={{ fontWeight: 700 }}>{d.c.titre}</div>
                <div style={{ color: P.faible, fontSize: 9.5 }}>{d.c.periode}</div>
                <div style={{ color: P.faible, fontSize: 9.5 }}>{d.v.libelle} : {d.v.texte}</div>
              </td>
              <td style={{ ...cellule, width: 104, textAlign: 'right' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: hausse ? '#1c7a42' : '#a42a1f' }}>
                  {hausse ? '▲' : '▼'} {Math.abs(arrondi)}%
                </div>
                {/* L'ecart en valeur : sur un document lu en reunion, c'est
                    souvent le chiffre qu'on cite. */}
                <div style={{ fontSize: 10, color: P.faible, marginTop: 1 }}>
                  {ecartEnClair(ecart, g.v.uniteAffichee)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EtatVide({ P, empile }) {
  return (
    <div style={{
      ...(empile ? { padding: '48px 20px' } : { position: 'absolute', inset: 0, padding: 24 }),
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 10, color: P.faible, textAlign: 'center',
    }}>
      <div style={{ fontSize: 30, opacity: .35 }}>◎</div>
      <div style={{ color: P.texte, fontSize: 13, letterSpacing: '.1em' }}>PAGE VIDE</div>
      <div style={{ fontSize: 11.5, lineHeight: 1.8, maxWidth: 470 }}>
        Fermez ce panneau, puis cliquez le petit <b style={{ color: P.accent }}>+</b> pose sur un chiffre,
        un cercle ou un graphe pour l'epingler ici.<br />
        Chaque element conserve les donnees et la periode du moment ou vous l'avez ajoute :
        changez de mois, epinglez a nouveau, les deux resteront cote a cote.
      </div>
    </div>
  );
}

/* --- Le graphe lui-meme, isole du reste -----------------------------------
   Ses entrees ne changent pour une carte donnee que si le mode d'affichage
   change (l'instantane, lui, est fige) : React.memo garantit donc qu'aucun
   graphe recharts n'est redessine quand on deplace, redimensionne ou remonte
   un element au premier plan. */
const ContenuCarte = React.memo(function ContenuCarte({ registre, kind, data, mode }) {
  const rendu = registre?.[kind];
  if (!rendu) return <div style={{ color: '#9aa2af', fontSize: 12 }}>Type d'element inconnu : {kind}</div>;
  return rendu(data, mode);
});

/* --- Un element epingle ---------------------------------------------------
   Il n'y a pas de carte : pas de conteneur, pas de fond, pas de bordure, pas
   d'ombre, et surtout pas de hauteur imposee — l'element fait exactement la
   taille de son graphe, donc aucun espace vide. Ne restent a l'ecran que deux
   lignes de legende (quoi, et sur quelle periode) puis le graphe lui-meme.

   Les commandes flottent en surimpression au survol : c'est la seule facon
   d'offrir "fermer" et "elargir" sans reintroduire une barre d'outils qui
   prendrait de la place en permanence. On attrape l'element n'importe ou pour
   le deplacer, sauf sur un bouton du graphe (bascule CA/Tickets par exemple). */
function Carte({ c, pos, registre, mode, P, empile, imprime, fige, refMesure, onDebutGlisse, onPremierPlan, onTaille, onRetirer, actif }) {
  const { peutRejouerCarte } = usePlanDeTravail();
  const [survol, setSurvol] = useState(false);
  const [rejeu, setRejeu] = useState(false);
  const basculerTaille = () => onTaille(c.id, { taille: c.taille === 'normal' ? 'grand' : 'normal' });
  const ellipse = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  const commencer = (e) => {
    if (empile) return;
    onPremierPlan(c.id);
    if (e.target.closest && e.target.closest('button')) return; // commande du graphe
    onDebutGlisse(e, c, pos, 'carte');
  };

  const commande = (label, onClick, titre, danger) => (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} title={titre} style={{
      // Cible elargie en pile : sur tactile, 18 pixels sont intouchables.
      width: empile ? 30 : 18, height: empile ? 30 : 18,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: P.panneau, border: `1px solid ${P.bord}`, borderRadius: empile ? 6 : 4, cursor: 'pointer',
      color: danger ? P.danger : P.texte, fontSize: empile ? 13 : 10, lineHeight: 1, padding: 0,
    }}>{label}</button>
  );

  // En pile, l'element occupe toute la largeur disponible et les commandes
  // restent affichees : il n'y a pas de survol sur un ecran tactile. Sur
  // papier en revanche, une croix de suppression n'a aucun sens.
  const visible = !imprime && !fige && (empile || survol);

  return (
    <section
      onPointerDown={commencer}
      onDoubleClick={empile ? undefined : basculerTaille}
      onMouseEnter={() => setSurvol(true)}
      onMouseLeave={() => setSurvol(false)}
      ref={refMesure}
      title={`Epingle a ${c.capture}`}
      style={empile ? {
        position: 'relative', width: '100%',
        // Un graphe coupe en deux par un saut de page est illisible.
        breakInside: 'avoid', pageBreakInside: 'avoid',
      } : {
        position: 'absolute', left: pos.x, top: pos.y, width: LARGEUR[c.taille],
        zIndex: c.z || 10, cursor: actif ? 'grabbing' : 'grab',
        // Un leger halo pendant le deplacement uniquement : il faut voir ce
        // qu'on deplace, mais rien ne doit subsister au repos.
        filter: actif ? `drop-shadow(${P.ombreGlisse})` : 'none',
        // Le navigateur ne doit pas interpreter l'appui comme un defilement.
        touchAction: 'none',
      }}>
      <div style={{ color: P.texte, fontSize: empile ? 12.5 : 10.5, fontWeight: 700, letterSpacing: '.02em', paddingRight: empile ? 38 : 0, ...ellipse }}>{c.titre}</div>
      <div style={{ color: P.accent, fontSize: empile ? 11 : 9.5, marginBottom: 3, paddingRight: empile ? 38 : 0, ...ellipse }}>{c.periode}</div>

      {/* Une donnee absente doit se voir SUR l'element concerne, et pas
          seulement dans un compteur au moment du rejeu : c'est en relisant la
          page, des jours plus tard, qu'on a besoin de le savoir. */}
      {(c.indisponible || c.vide) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5,
          border: `1px solid ${c.indisponible ? P.danger : P.bord}`, borderRadius: 6,
          padding: '4px 7px', fontSize: 9.5, lineHeight: 1.35,
          color: c.indisponible ? P.danger : P.faible,
        }}>
          <span style={{ flexShrink: 0 }}>{c.indisponible ? '⚠' : '○'}</span>
          <span>
            {c.indisponible || c.vide}
            {c.indisponible && <span style={{ opacity: .8 }}> — chiffres d'origine conserves</span>}
          </span>
        </div>
      )}

      <ContenuCarte registre={registre} kind={c.kind} data={c.data} mode={mode} />

      {visible && (
        <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: 3 }}>
          {/* Le bouton s'affiche meme sans `requete` : un element epingle
              avant cette fonctionnalite n'en a pas, et le masquer sans rien
              dire laisse chercher une commande qui n'existe pas. Le panneau
              explique alors quoi faire. */}
          {peutRejouerCarte && !empile &&
            commande('⟲', () => setRejeu((v) => !v),
              'Changer la periode ou la boutique de cet element seul')}
          {!empile && commande(c.taille === 'normal' ? '⤢' : '⤡', basculerTaille, 'Elargir / retrecir')}
          {commande('✕', () => onRetirer(c.id), 'Retirer du plan', true)}
        </div>
      )}

      {rejeu && <RejeuCarte c={c} P={P} onFermer={() => setRejeu(false)} />}
    </section>
  );
}

/* --- Changer la periode ou la boutique d'un seul element ------------------
   « Comparer » cree une copie posee juste a droite : deux fois la meme mesure
   sur deux perimetres, voisines, et l'ecart s'affiche de lui-meme entre elles.
   C'est le geste qu'on veut la plupart du temps — remplacer sur place ne sert
   qu'a corriger une carte epinglee sur la mauvaise periode. */
function RejeuCarte({ c, P, onFermer }) {
  const { rejouerCarte, boutiques, deduireRequete } = usePlanDeTravail();
  // Retrouvee si elle n'a pas ete enregistree a l'epinglage.
  const requete = c.requete || deduireRequete?.(c) || null;
  const { estMobile } = useEcran();
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [du, setDu] = useState(aujourdhui);
  const [au, setAu] = useState(aujourdhui);
  // `undefined` = ne pas toucher au perimetre ; '' = tout le reseau.
  const [boutique, setBoutique] = useState(requete?.boutique ?? '');
  const [etat, setEtat] = useState(null);

  // Les anneaux de repartition et la heatmap portent sur le reseau entier :
  // proposer une boutique n'aurait aucun sens.
  const perimetrable = !['repartition', 'heatmap', 'frequentation'].includes(c.kind);

  const lancer = async (aCote) => {
    setEtat({ encours: true });
    try {
      const r = await rejouerCarte(c.id, {
        from: du > au ? au : du, to: du > au ? du : au,
        ...(perimetrable ? { boutique: boutique || null } : {}),
      }, { aCote });
      setEtat({ ok: r });
      setTimeout(onFermer, aCote ? 1200 : 900);
    } catch (e) {
      setEtat({ erreur: e.message });
    }
  };

  const style = {
    champ: {
      background: P.surface, color: P.texte, border: `1px solid ${P.bord}`, borderRadius: 6,
      padding: estMobile ? '7px 8px' : '4px 6px', fontSize: estMobile ? 16 : 11, fontFamily: MONO,
    },
    action: (principal) => ({
      cursor: 'pointer', borderRadius: 6, fontFamily: MONO, fontSize: 10.5, fontWeight: 700,
      padding: '6px 10px', letterSpacing: '.04em',
      background: principal ? P.accent : 'transparent',
      color: principal ? P.panneau : P.texte,
      border: `1px solid ${principal ? P.accent : P.bord}`,
    }),
  };

  return (
    <div onPointerDown={(e) => e.stopPropagation()} style={{
      position: 'absolute', top: 24, right: 0, zIndex: 10000, width: 250,
      background: P.panneau, border: `1px solid ${P.bordFort}`, borderRadius: 8,
      padding: 10, boxShadow: P.ombre, display: 'grid', gap: 7,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ flex: 1, color: P.accent, fontSize: 9.5, letterSpacing: '.08em' }}>REJOUER CET ELEMENT</span>
        <button onClick={onFermer} style={{ background: 'transparent', border: 'none', color: P.faible, cursor: 'pointer', fontSize: 12 }}>✕</button>
      </div>

      {!requete ? (
        /* Un element ne peut etre rejoue que s'il a garde la QUESTION qui l'a
           produit. Ceux epingles avant cette fonctionnalite ne portent que
           leur reponse : on ne peut pas la deviner apres coup. */
        <div style={{ color: P.faible, fontSize: 10, lineHeight: 1.5 }}>
          La question qui a produit cet element n'a pas pu etre retrouvee.
          <br /><br />
          <b style={{ color: P.texte }}>Re-epinglez-le depuis le tableau de bord</b> et
          il deviendra rejouable sur toute periode et toute boutique.
        </div>
      ) : (
      <>
      <div style={{ display: 'flex', gap: 6 }}>
        <input type="date" max={aujourdhui} value={du} onChange={(e) => setDu(e.target.value)} style={{ ...style.champ, flex: 1 }} />
        <input type="date" max={aujourdhui} value={au} onChange={(e) => setAu(e.target.value)} style={{ ...style.champ, flex: 1 }} />
      </div>

      {perimetrable && (
        <select value={boutique} onChange={(e) => setBoutique(e.target.value)} style={style.champ}>
          <option value="">Tout le reseau</option>
          {boutiques.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      )}

      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => lancer(true)} disabled={etat?.encours} style={{ ...style.action(true), flex: 1 }}>
          {etat?.encours ? '…' : 'COMPARER'}
        </button>
        <button onClick={() => lancer(false)} disabled={etat?.encours} style={style.action(false)}>
          REMPLACER
        </button>
      </div>

      {etat?.erreur && <div style={{ color: P.danger, fontSize: 10, lineHeight: 1.4 }}>{etat.erreur}</div>}
      {etat?.ok?.vide && <div style={{ color: P.faible, fontSize: 10 }}>{etat.ok.vide}</div>}
      {!etat && (
        <div style={{ color: P.faible, fontSize: 9.5, lineHeight: 1.4 }}>
          « Comparer » pose une copie a droite et affiche l'ecart entre les deux.
        </div>
      )}
      </>
      )}
    </div>
  );
}

/* --- Une note libre -------------------------------------------------------
   Contrairement aux graphes, une note DOIT avoir un fond : sans support
   visible, on ne saurait pas ou cliquer pour ecrire. Elle se deplace par sa
   poignee (la bande du haut) et non par son corps, qui sert a la saisie.

   Le texte est tenu en etat local et consigne dans la page apres une pause de
   frappe : ecrire dans l'etat partage a chaque touche ferait re-rendre tout
   l'espace de travail et declencherait un envoi au serveur par caractere. */
function Note({ n, pos, P, empile, imprime, fige, refMesure, onDebutGlisse, onPremierPlan, onMaj, onRetirer, actif }) {
  const [texte, setTexte] = useState(n.texte || '');
  const [survol, setSurvol] = useState(false);

  useEffect(() => {
    if (texte === (n.texte || '')) return undefined;
    const t = setTimeout(() => onMaj(n.id, { texte }), 500);
    return () => clearTimeout(t);
    // `n.texte` volontairement hors dependances : le reinclure relancerait le
    // minuteur a chaque consignation et empecherait la frappe de se stabiliser.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texte, n.id, onMaj]);

  const basculerTaille = () => onMaj(n.id, { taille: n.taille === 'normal' ? 'grand' : 'normal' });

  const commande = (label, onClick, titre, danger) => (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} title={titre} style={{
      // Cible elargie en pile : au doigt, 16 pixels sont intouchables.
      width: empile ? 30 : 16, height: empile ? 30 : 16,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'transparent', border: 'none', borderRadius: 3, cursor: 'pointer',
      color: danger ? P.danger : P.noteAccent, fontSize: empile ? 14 : 10, lineHeight: 1, padding: 0,
    }}>{label}</button>
  );

  const visible = !imprime && !fige && (empile || survol);

  return (
    <section
      ref={refMesure}
      onPointerDown={empile ? undefined : () => onPremierPlan(n.id)}
      onMouseEnter={() => setSurvol(true)}
      onMouseLeave={() => setSurvol(false)}
      style={{
        display: 'flex', flexDirection: 'column',
        background: P.noteFond, border: `1px solid ${P.noteBord}`, borderRadius: 6,
        boxShadow: imprime ? 'none' : (actif ? P.ombreGlisse : P.ombre),
        ...(empile
          ? { position: 'relative', width: '100%', breakInside: 'avoid', pageBreakInside: 'avoid' }
          : {
            position: 'absolute', left: pos.x, top: pos.y,
            width: LARGEUR_NOTE[n.taille], zIndex: n.z || 10, touchAction: 'none',
          }),
      }}>
      <header
        onPointerDown={(e) => { if (!empile && !e.target.closest('button')) onDebutGlisse(e, n, pos, 'note'); }}
        onDoubleClick={empile ? undefined : basculerTaille}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: empile ? '3px 6px 2px 10px' : '4px 6px 2px 8px',
          cursor: empile ? 'default' : (actif ? 'grabbing' : 'grab'), flexShrink: 0,
        }}>
        {!empile && <span style={{ color: P.noteAccent, fontSize: 9, opacity: .8 }}>⠿</span>}
        <span style={{ flex: 1, color: P.noteAccent, fontSize: 9.5, letterSpacing: '.06em', opacity: .8 }}>NOTE</span>
        <span style={{ display: 'flex', gap: 2, opacity: visible ? 1 : 0, transition: 'opacity .15s' }}>
          {!empile && commande(n.taille === 'normal' ? '⤢' : '⤡', basculerTaille, 'Elargir / retrecir')}
          {commande('✕', () => onRetirer(n.id), 'Supprimer cette note', true)}
        </span>
      </header>

      {imprime ? (
        /* Un textarea n'imprime que sa partie visible : le reste du texte est
           purement et simplement perdu sur la feuille. On rend donc la note en
           texte simple, qui s'etend sur toute sa hauteur. */
        <div style={{
          padding: '2px 10px 10px', color: P.noteTexte, fontFamily: 'inherit',
          fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>{texte}</div>
      ) : (
        <textarea
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          onBlur={() => onMaj(n.id, { texte })}
          readOnly={fige}
          placeholder="Ecrire une note…"
          spellCheck={false}
          style={{
            width: '100%', height: empile ? 130 : HAUTEUR_NOTE[n.taille],
            resize: 'none', border: 'none', outline: 'none',
            background: 'transparent', color: P.noteTexte, padding: '2px 10px 10px',
            fontFamily: 'inherit',
            // 16px minimum sur mobile : en dessous, iOS zoome automatiquement a
            // la mise au point et casse la mise en page.
            fontSize: empile ? 16 : 12,
            lineHeight: 1.6, boxSizing: 'border-box',
          }} />
      )}
    </section>
  );
}

